/**
 * AUSTRAC Compliance Forms Library — Static Data
 * Extracted from Forms.tsx for maintainability.
 */

export interface FormItem {
	id: string;
	name: string;
	description: string;
	category: string;
	version: string;
	lastUpdated: string;
	fileType: 'PDF' | 'DOCX' | 'XLSX' | 'Online';
	fileSize?: string;
	mandatory: boolean;
	downloadUrl?: string;
	tags?: string[];
}

export interface FormCategory {
	id: string;
	title: string;
	description: string;
	icon: string;
	forms: FormItem[];
}

/** All AUSTRAC compliance form categories with actual AUSTRAC URLs. */
export const FORM_CATEGORIES: FormCategory[] = [
	{
		id: 'cdd',
		title: 'Customer Due Diligence',
		description: 'Forms and guidance for customer identification and verification',
		icon: '👤',
		forms: [
			{
				id: 'cdd-001',
				name: 'Customer Identification (KYC) Guide',
				description: 'Official AUSTRAC guidance on Know Your Customer (KYC) procedures for identifying and verifying individual customers.',
				category: 'CDD',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/customer-identification-and-verification/customer-identification-know-your-customer-kyc',
				tags: ['Individual', 'KYC', 'Identity'],
			},
			{
				id: 'cdd-002',
				name: 'Beneficial Owners Identification',
				description: 'AUSTRAC guidance on identifying and verifying beneficial owners of corporate entities and trusts.',
				category: 'CDD',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/customer-identification-and-verification/beneficial-owners',
				tags: ['Corporate', 'Entity', 'Beneficial Ownership'],
			},
			{
				id: 'cdd-003',
				name: 'Customer ID & Verification Easy Reference Guide',
				description: 'Quick reference guide for customer identification and verification procedures.',
				category: 'CDD',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/customer-identification-and-verification/customer-identification-and-verification-easy-reference-guide',
				tags: ['Reference', 'Quick Guide', 'Verification'],
			},
			{
				id: 'cdd-004',
				name: 'Enhanced Customer Due Diligence (ECDD) Program',
				description: 'AUSTRAC guidance on enhanced due diligence for high-risk customers and situations.',
				category: 'CDD',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/amlctf-programs/enhanced-customer-due-diligence-ecdd-program',
				tags: ['EDD', 'High Risk', 'Enhanced'],
			},
			{
				id: 'cdd-005',
				name: 'Source of Funds and Source of Wealth',
				description: 'AUSTRAC guidance on collecting and verifying source of funds and wealth information.',
				category: 'CDD',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: false,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/customer-identification-and-verification/source-funds-and-source-wealth',
				tags: ['Source of Funds', 'Wealth', 'Verification'],
			},
			{
				id: 'cdd-006',
				name: 'Politically Exposed Persons (PEPs)',
				description: 'AUSTRAC guidance on identifying and managing politically exposed persons.',
				category: 'CDD',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/customer-identification-and-verification/politically-exposed-persons-peps',
				tags: ['PEP', 'High Risk', 'Political'],
			},
			{
				id: 'cdd-007',
				name: 'Assisting Customers Without Standard ID',
				description: 'Guidance for customers who don\'t have standard forms of identification.',
				category: 'CDD',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: false,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/customer-identification-and-verification/assisting-customers-who-dont-have-standard-forms-identification',
				tags: ['Alternative ID', 'Vulnerable', 'Assistance'],
			},
		],
	},
	{
		id: 'reporting',
		title: 'AUSTRAC Reporting',
		description: 'Official reporting forms and guidance for regulatory submissions',
		icon: '📊',
		forms: [
			{
				id: 'rep-001',
				name: 'Suspicious Matter Reports (SMRs)',
				description: 'Submit SMRs when you suspect a customer or transaction is related to criminal activity. Due within 24 hours for terrorism financing, 3 business days otherwise.',
				category: 'Reporting',
				version: '2026',
				lastUpdated: '2025-11-25',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/reporting/suspicious-matter-reports-smrs',
				tags: ['SMR', 'Suspicious Activity', 'AUSTRAC'],
			},
			{
				id: 'rep-002',
				name: 'Threshold Transaction Reports (TTRs)',
				description: 'Report cash transactions of $10,000 or more. Due within 10 business days after the transaction.',
				category: 'Reporting',
				version: '2026',
				lastUpdated: '2025-11-25',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/reporting/reporting-transactions-10000-and-over-threshold-transaction-reports-ttrs',
				tags: ['TTR', 'Cash Transaction', 'Threshold', '$10000'],
			},
			{
				id: 'rep-003',
				name: 'International Funds Transfer Instructions (IFTIs)',
				description: 'Report international electronic funds transfers of any value. Due within 10 business days.',
				category: 'Reporting',
				version: '2026',
				lastUpdated: '2025-11-25',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/reporting/money-transferred-and-overseas-international-funds-transfer-instruction-ifti-reports',
				tags: ['IFTI', 'International Transfer', 'Cross-border'],
			},
			{
				id: 'rep-004',
				name: 'AUSTRAC Compliance Reports',
				description: 'Compliance reports submitted to AUSTRAC when required, detailing how you comply with AML/CTF obligations.',
				category: 'Reporting',
				version: '2026',
				lastUpdated: '2025-11-25',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/reporting/austrac-compliance-reports',
				tags: ['Compliance Report', 'Annual', 'AUSTRAC'],
			},
			{
				id: 'rep-005',
				name: 'Cross-Border Movement (CBM) Reports',
				description: 'Report physical currency movements of $10,000+ into or out of Australia.',
				category: 'Reporting',
				version: '2026',
				lastUpdated: '2025-11-25',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.austrac.gov.au/business/how-comply-guidance-and-resources/reporting/money-brought-or-out-australia-cbm',
				tags: ['CBM', 'Cross-border', 'Physical Currency'],
			},
			{
				id: 'rep-006',
				name: 'AUSTRAC Online Portal',
				description: 'Access AUSTRAC Online to submit transaction reports, manage your account, and view reporting history.',
				category: 'Reporting',
				version: '2026',
				lastUpdated: '2025-11-21',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://online.apps.austrac.gov.au/',
				tags: ['Portal', 'Online', 'Submit Reports'],
			},
			{
				id: 'rep-007',
				name: 'Top Tips to Improve Your Reports',
				description: 'AUSTRAC guidance on improving the quality and accuracy of your reports.',
				category: 'Reporting',
				version: '2026',
				lastUpdated: '2025-11-25',
				fileType: 'Online',
				mandatory: false,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/reporting/top-tips-improve-your-reports',
				tags: ['Tips', 'Quality', 'Accuracy'],
			},
		],
	},
	{
		id: 'risk',
		title: 'Risk Assessment',
		description: 'Guidance and resources for ML/TF risk assessment',
		icon: '⚠️',
		forms: [
			{
				id: 'risk-001',
				name: 'ML/TF Risk Assessment Guide',
				description: 'AUSTRAC guidance on conducting money laundering and terrorism financing risk assessments.',
				category: 'Risk',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/amlctf-programs/money-launderingterrorism-financing-risk-assessment',
				tags: ['Risk Assessment', 'ML/TF', 'Guide'],
			},
			{
				id: 'risk-002',
				name: 'Transaction Monitoring Program',
				description: 'AUSTRAC guidance on establishing and maintaining transaction monitoring programs.',
				category: 'Risk',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/amlctf-programs/transaction-monitoring',
				tags: ['Transaction Monitoring', 'Program', 'Ongoing'],
			},
			{
				id: 'risk-003',
				name: 'High-Risk Countries, Regions and Groups',
				description: 'AUSTRAC guidance on dealing with high-risk jurisdictions and groups.',
				category: 'Risk',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/customer-identification-and-verification/high-risk-countries-regions-and-groups',
				tags: ['High Risk', 'Countries', 'Jurisdictions'],
			},
			{
				id: 'risk-004',
				name: 'Preventing Financial Crime - Risk-Based Approach',
				description: 'AUSTRAC guidance on using a risk-based approach to prevent financial crime.',
				category: 'Risk',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/preventing-financial-crime-using-risk-based-approach',
				tags: ['Risk-Based', 'Prevention', 'Financial Crime'],
			},
			{
				id: 'risk-005',
				name: 'Risk Insights for Accountants',
				description: 'Industry-specific risk insights and indicators of suspicious activity for accountants.',
				category: 'Risk',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: false,
				downloadUrl: 'https://www.austrac.gov.au/business/how-comply-guidance-and-resources/guidance-resources/risk-insights-and-indicators-suspicious-activity-accountants',
				tags: ['Accountants', 'Industry', 'Risk Insights'],
			},
			{
				id: 'risk-006',
				name: 'Risk Insights for Real Estate Sector',
				description: 'Industry-specific risk insights and indicators of suspicious activity for real estate professionals.',
				category: 'Risk',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: false,
				downloadUrl: 'https://www.austrac.gov.au/business/how-comply-guidance-and-resources/guidance-resources/risk-insights-and-indicators-suspicious-activity-real-estate-sector',
				tags: ['Real Estate', 'Industry', 'Risk Insights'],
			},
			{
				id: 'risk-007',
				name: 'Risk Insights for Legal Professionals',
				description: 'Industry-specific risk insights and indicators of suspicious activity for legal professionals.',
				category: 'Risk',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: false,
				downloadUrl: 'https://www.austrac.gov.au/business/how-comply-guidance-and-resources/guidance-resources/risk-insights-and-indicators-suspicious-activity-legal-professionals',
				tags: ['Legal', 'Lawyers', 'Risk Insights'],
			},
		],
	},
	{
		id: 'programs',
		title: 'AML/CTF Programs',
		description: 'Guidance for establishing and maintaining AML/CTF programs',
		icon: '📋',
		forms: [
			{
				id: 'prog-001',
				name: 'AML/CTF Programs Overview',
				description: 'AUSTRAC guidance on developing and maintaining your AML/CTF program.',
				category: 'Programs',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/amlctf-programs',
				tags: ['AML/CTF Program', 'Compliance', 'Overview'],
			},
			{
				id: 'prog-002',
				name: 'Record-Keeping Requirements',
				description: 'AUSTRAC guidance on record-keeping obligations under AML/CTF legislation.',
				category: 'Programs',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/record-keeping',
				tags: ['Records', 'Retention', 'Compliance'],
			},
			{
				id: 'prog-003',
				name: 'Exemptions and Modifications',
				description: 'Information about available exemptions and modifications to AML/CTF requirements.',
				category: 'Programs',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: false,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/exemptions-and-modifications',
				tags: ['Exemptions', 'Modifications', 'Relief'],
			},
			{
				id: 'prog-004',
				name: 'Guidance on Outsourcing',
				description: 'Using outsourcing to help meet your AML/CTF obligations.',
				category: 'Programs',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: false,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/using-outsourcing-help-meet-your-amlctf-obligations',
				tags: ['Outsourcing', 'Third Party', 'Service Providers'],
			},
			{
				id: 'prog-005',
				name: 'Consequences of Not Complying',
				description: 'Information about enforcement actions and penalties for non-compliance.',
				category: 'Programs',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.austrac.gov.au/business/core-guidance/consequences-not-complying',
				tags: ['Enforcement', 'Penalties', 'Consequences'],
			},
		],
	},
	{
		id: 'registration',
		title: 'Registration & Account',
		description: 'AUSTRAC Online account management and registration',
		icon: '🔐',
		forms: [
			{
				id: 'reg-001',
				name: 'Enrol or Register with AUSTRAC',
				description: 'Information on how to enrol or register your business with AUSTRAC.',
				category: 'Registration',
				version: '2026',
				lastUpdated: '2025-11-21',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.austrac.gov.au/business/new-to-austrac/enrol-or-register',
				tags: ['Enrol', 'Register', 'New Business'],
			},
			{
				id: 'reg-002',
				name: 'Manage Your AUSTRAC Account',
				description: 'Guide to managing your AUSTRAC Online account settings and users.',
				category: 'Registration',
				version: '2026',
				lastUpdated: '2025-11-21',
				fileType: 'Online',
				mandatory: false,
				downloadUrl: 'https://www.austrac.gov.au/business/austrac-online/manage-your-account',
				tags: ['Account', 'Manage', 'Settings'],
			},
			{
				id: 'reg-003',
				name: 'Update Your Details',
				description: 'How to update your business details in AUSTRAC Online.',
				category: 'Registration',
				version: '2026',
				lastUpdated: '2025-11-21',
				fileType: 'Online',
				mandatory: false,
				downloadUrl: 'https://www.austrac.gov.au/business/austrac-online/update-your-details',
				tags: ['Update', 'Details', 'Changes'],
			},
			{
				id: 'reg-004',
				name: 'AUSTRAC Online User Guides',
				description: 'Comprehensive user guides for navigating AUSTRAC Online.',
				category: 'Registration',
				version: '2026',
				lastUpdated: '2025-11-21',
				fileType: 'Online',
				mandatory: false,
				downloadUrl: 'https://www.austrac.gov.au/business/austrac-online/austrac-online-user-guides',
				tags: ['User Guide', 'Help', 'Instructions'],
			},
			{
				id: 'reg-005',
				name: 'Renew Your Registration',
				description: 'Information on renewing your AUSTRAC registration.',
				category: 'Registration',
				version: '2026',
				lastUpdated: '2025-11-21',
				fileType: 'Online',
				mandatory: false,
				downloadUrl: 'https://www.austrac.gov.au/business/austrac-online/renew-your-registration',
				tags: ['Renew', 'Registration', 'Annual'],
			},
		],
	},
	{
		id: 'legislation',
		title: 'Legislation & Rules',
		description: 'Access to AML/CTF legislation and rules',
		icon: '⚖️',
		forms: [
			{
				id: 'leg-001',
				name: 'AML/CTF Act (Latest Version)',
				description: 'The Anti-Money Laundering and Counter-Terrorism Financing Act 2006.',
				category: 'Legislation',
				version: '2026',
				lastUpdated: '2025-10-20',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.legislation.gov.au/C2006A00169/latest/text',
				tags: ['Act', 'Legislation', 'Law'],
			},
			{
				id: 'leg-002',
				name: 'AML/CTF Rules (Latest Version)',
				description: 'The Anti-Money Laundering and Counter-Terrorism Financing Rules.',
				category: 'Legislation',
				version: '2026',
				lastUpdated: '2025-09-29',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.legislation.gov.au/F2007L01000/latest/text',
				tags: ['Rules', 'Legislation', 'Regulations'],
			},
			{
				id: 'leg-003',
				name: 'AML/CTF Reforms Guidance',
				description: 'Guidance on the new AML/CTF reforms effective from 31 March 2026.',
				category: 'Legislation',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: true,
				downloadUrl: 'https://www.austrac.gov.au/node/1370',
				tags: ['Reforms', '2026', 'New Requirements'],
			},
			{
				id: 'leg-004',
				name: 'Industry Contribution Legislation',
				description: 'Information about industry contribution levy requirements.',
				category: 'Legislation',
				version: '2026',
				lastUpdated: '2025-10-16',
				fileType: 'Online',
				mandatory: false,
				downloadUrl: 'https://www.austrac.gov.au/business/legislation/industry-contribution-legislation',
				tags: ['Levy', 'Contribution', 'Fees'],
			},
		],
	},
];

/** Helper: icon for a given file type */
export const getFileTypeIcon = (fileType: FormItem['fileType']): string => {
	switch (fileType) {
		case 'PDF':
			return '📄';
		case 'DOCX':
			return '📝';
		case 'XLSX':
			return '📊';
		case 'Online':
			return '🌐';
		default:
			return '📎';
	}
};

/** Helper: accent colour for a given file type */
export const getFileTypeColor = (fileType: FormItem['fileType']): string => {
	switch (fileType) {
		case 'PDF':
			return '#dc2626';
		case 'DOCX':
			return '#2563eb';
		case 'XLSX':
			return '#16a34a';
		case 'Online':
			return '#7c3aed';
		default:
			return '#6b7280';
	}
};
